import React, { useState, useEffect } from 'react';
import {
  X,
  Users,
  User,
  Building,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Clipboard,
  FileText,
  Package,
  Clock,
  Activity,
  Boxes,
  Settings,
  Calendar,
  Factory,
  QrCode,
  Hash,
  Eye,
  ChevronDown,
  ChevronUp,
  Timer,
  BarChart3,
  Layers,
  Tag,
  CalendarClock,
  Database,
  Download,
  Printer
} from 'lucide-react';
import toast from 'react-hot-toast';
import { mapSapDataToProductionLotPayload } from '../../utils/productionLotMapper';
import { createProductionLot } from '../../api/productionTask.api';

const AssignTaskModal = ({
  isOpen,
  onClose,
  productionLot,
  onAssigned,
  lookupMode = 'taskId',
  cachedUsers = null,
  cachedTeams = null
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [fetchingTeams, setFetchingTeams] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState(null);
  const [assignmentData, setAssignmentData] = useState({
    userIds: [],  // Changed to array for multiple users
    teamIds: []   // Changed to array for multiple teams
  });

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    schedule: false,
    barcode: false,
    activities: false,
    materialInputs: false,
    materialOutputs: false,
    productionTasks: false,
    confirmationGroups: false,
    metadata: false
  });

  // Real users and teams data from API
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fetch real users and teams from API when modal opens (skip if cached data provided)
  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        // Use cached data if available (avoids duplicate /lookup calls)
        if (cachedUsers?.length) {
          setUsers(cachedUsers);
          setFetchingUsers(false);
        } else {
          setFetchingUsers(true);
          try {
            const usersResponse = await fetch('/api/auth/lookup', {
              headers: getAuthHeaders()
            });

            if (!usersResponse.ok) {
              throw new Error(`HTTP error! status: ${usersResponse.status}`);
            }

            const usersResult = await usersResponse.json();

            if (usersResult.isSuccess) {
              const usersData = usersResult.data || [];
              const formattedUsers = usersData.map(user => ({
                id: user.id,
                name: user.name
              }));
              setUsers(formattedUsers);
            }
          } catch (usersError) {
            console.error('Failed to fetch users:', usersError);
            setUsers([]);
          } finally {
            setFetchingUsers(false);
          }
        }

        // Use cached teams if available
        if (cachedTeams?.length) {
          setTeams(cachedTeams);
          setFetchingTeams(false);
        } else {
          setFetchingTeams(true);
          // Fetch teams
          try {
            const teamsResponse = await fetch('/api/teams/lookup', {
              headers: getAuthHeaders()
            });

            if (teamsResponse.status === 401) {
              console.warn('Unauthorized to fetch teams');
              setTeams([]);
              return;
            }

            if (!teamsResponse.ok) {
              throw new Error(`HTTP error! status: ${teamsResponse.status}`);
            }

            const teamsResult = await teamsResponse.json();

            let teamsArray = [];
            if (Array.isArray(teamsResult)) {
              teamsArray = teamsResult;
            } else if (teamsResult && teamsResult.data && Array.isArray(teamsResult.data)) {
              teamsArray = teamsResult.data;
            } else if (teamsResult && teamsResult.isSuccess && teamsResult.data) {
              teamsArray = teamsResult.data;
            }

            setTeams(teamsArray || []);

          } catch (teamsError) {
            console.error('Failed to fetch teams:', teamsError);
            setTeams([]);
          } finally {
            setFetchingTeams(false);
          }
        }

        // Reset other states
        setAssignmentData({ userIds: [], teamIds: [] });
        setError(null);
        setSuccess(false);
        setAssignmentResult(null);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, cachedUsers, cachedTeams]);

  // Get selected user names
  const getSelectedUserNames = () => {
    if (!assignmentData.userIds || assignmentData.userIds.length === 0) return [];
    return assignmentData.userIds
      .map(userId => {
        const user = users.find(u => u.id === userId);
        return user ? user.name : null;
      })
      .filter(name => name !== null);
  };

  // Get selected team names
  const getSelectedTeamNames = () => {
    if (!assignmentData.teamIds || assignmentData.teamIds.length === 0) return [];
    return assignmentData.teamIds
      .map(teamId => {
        const team = teams.find(t => t.id === teamId);
        return team ? team.name : null;
      })
      .filter(name => name !== null);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Format duration helper
  const formatDuration = (duration) => {
    if (!duration) return '-';
    if (duration === 'PT0S') return '0';
    return duration
      .replace('PT', '')
      .replace('H', 'h ')
      .replace('M', 'm ')
      .replace('S', 's');
  };

  // Get status color class
  const getStatusColorClass = (statusCode) => {
    switch (statusCode) {
      case '4': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
      case '3': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case '2': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
      case '1': return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600';
    }
  };

  // Get status icon
  const getStatusIcon = (statusCode) => {
    switch (statusCode) {
      case '4': return <CheckCircle className="w-3 h-3" />;
      case '3': return <Clock className="w-3 h-3" />;
      case '2': return <Timer className="w-3 h-3" />;
      default: return <Tag className="w-3 h-3" />;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if ((!assignmentData.userIds || assignmentData.userIds.length === 0) &&
      (!assignmentData.teamIds || assignmentData.teamIds.length === 0)) {
      setError('Please select at least a user or a team to assign the task to');
      return;
    }

    if (!productionLot) {
      setError('No production lot data available');
      return;
    }

    setLoading(true);
    setError(null);

    // Show loading toast
    const loadingToast = toast.loading('Importing and assigning production lot...', {
      duration: Infinity
    });

    try {
      // Prepare task assignments from the selected users/teams
      const taskAssignments = [];

      // Create assignments for each selected user
      if (assignmentData.userIds && assignmentData.userIds.length > 0) {
        assignmentData.userIds.forEach(userId => {
          taskAssignments.push({
            id: null,
            productionTaskId: productionLot.productionTasks?.[0]?.productionTaskId || null,
            productionTask: null,
            userId: userId,
            teamId: null
          });
        });
      }

      // Create assignments for each selected team
      if (assignmentData.teamIds && assignmentData.teamIds.length > 0) {
        assignmentData.teamIds.forEach(teamId => {
          taskAssignments.push({
            id: null,
            productionTaskId: productionLot.productionTasks?.[0]?.productionTaskId || null,
            productionTask: null,
            userId: null,
            teamId: teamId
          });
        });
      }

      // Map the SAP data to the required payload format
      const payload = mapSapDataToProductionLotPayload(productionLot, taskAssignments);

      console.log('Sending payload to API:', payload);

      // Use the API function instead of direct fetch
      const response = await createProductionLot(payload);

      console.log('✅ Production lot created successfully:', response);

      // Dismiss loading toast and show success toast
      toast.dismiss(loadingToast);

      const selectedUserNames = getSelectedUserNames();
      const selectedTeamNames = getSelectedTeamNames();

      toast.success(
        <div>
          <strong className="block mb-1">Production Lot Imported Successfully!</strong>
          <span className="text-sm opacity-90">
            Lot ID: {productionLot?.productionLotId || productionLot?.productionLotID}
          </span>
          {selectedUserNames.length > 0 && (
            <span className="text-sm block mt-1 opacity-80">
              Assigned to users: {selectedUserNames.join(', ')}
            </span>
          )}
          {selectedTeamNames.length > 0 && (
            <span className="text-sm block opacity-80">
              Teams: {selectedTeamNames.join(', ')}
            </span>
          )}
        </div>,
        {
          duration: 5000,
          icon: '✅'
        }
      );

      const result = {
        success: true,
        taskId: response.id || response.productionTaskId || `task-${Date.now()}`,
        productionTaskId: productionLot.productionLotId,
        productionLotId: productionLot.productionLotId,
        productionLotUUID: productionLot.productionLotUUID,
        productionOrderId: productionLot.productionOrderId,
        assignedTo: {
          userIds: assignmentData.userIds || [],
          teamIds: assignmentData.teamIds || [],
          userNames: selectedUserNames,
          teamNames: selectedTeamNames
        },
        timestamp: new Date().toISOString(),
        message: 'Production lot successfully imported from SAP and assigned',
        data: response
      };

      setAssignmentResult(result);
      setSuccess(true);

      // Notify parent component
      if (onAssigned) {
        onAssigned(result);
      }

      // Auto-close modal after 3 seconds
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (err) {
      console.error('❌ Assignment failed:', err);

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // Extract meaningful error message from the error object
      let errorMessage = 'Failed to assign task. Please try again.';
      let detailedError = '';

      // Check if the error has a response property (from axios/fetch)
      if (err.response) {
        // The request was made and the server responded with a status code
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);

        // Try to extract error message from response data
        if (err.response.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error;
          } else if (err.response.data.title) {
            errorMessage = err.response.data.title;
          } else if (err.response.data.detail) {
            errorMessage = err.response.data.detail;
          } else {
            // If it's an object but doesn't have standard fields, stringify it
            try {
              detailedError = JSON.stringify(err.response.data);
            } catch {
              detailedError = 'Unknown error format';
            }
          }
        }
      } else if (err.request) {
        // The request was made but no response was received
        errorMessage = 'No response received from server. Please check your network connection.';
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage = err.message || 'An unexpected error occurred';
      }

      // Check for duplicate ProductionLotId error
      const isDuplicateError = err.isDuplicate ||
        errorMessage.includes('already exists') ||
        errorMessage.includes('duplicate') ||
        errorMessage.includes('ProductionLotId') ||
        (productionLot?.productionLotId && errorMessage.includes(productionLot.productionLotId));

      if (isDuplicateError) {
        const lotId = productionLot?.productionLotId || productionLot?.productionLotID || 'Unknown';

        // Extract the specific error message if available
        const specificError = errorMessage.includes('already exists') ? errorMessage :
          `Production Lot ID '${lotId}' already exists in the system.`;

        // Show duplicate error toast with detailed message
        toast.error(
          <div>
            <strong className="block mb-1 text-red-800 dark:text-red-400">Production Lot Already Exists</strong>
            <span className="text-sm block">Lot ID: {lotId}</span>
            <span className="text-xs block mt-1 opacity-75">This lot has already been imported.</span>
          </div>,
          {
            duration: 6000,
          }
        );

        // Set meaningful error message in modal
        setError(specificError);
      } else {
        // Show general error toast
        toast.error(
          <div>
            <strong className="block mb-1">Assignment Failed</strong>
            <span className="text-sm opacity-90">{errorMessage}</span>
            {detailedError && (
              <span className="text-xs block mt-2 opacity-70">{detailedError}</span>
            )}
          </div>,
          {
            duration: 5000,
            icon: '❌'
          }
        );

        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-2 sm:p-4 animate-in fade-in duration-200 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-6xl mx-auto my-auto transform transition-all animate-in zoom-in-95 duration-200 max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Assign Production Task</h2>
                <p className="text-blue-100 text-xs sm:text-sm">
                  Import from SAP and assign to multiple users/teams
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-white/20 rounded-lg transition-colors"
              disabled={loading}
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {success ? (
            /* ✅ SUCCESS STATE */
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">Successfully Assigned!</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base mb-3 sm:mb-4">
                  {assignmentResult?.message || 'The production task has been imported from SAP and assigned.'}
                </p>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    {lookupMode === 'taskId' ? (
                      <Clipboard className="w-4 h-4 text-blue-600" />
                    ) : (
                      <FileText className="w-4 h-4 text-purple-600" />
                    )}
                    <span className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                      {lookupMode === 'taskId' ? 'Production Lot ID' : 'Order ID'}:
                    </span>
                  </div>
                  <p className="font-mono text-base sm:text-lg font-bold text-blue-600 truncate">
                    {productionLot?.productionLotId || productionLot?.productionLotID || 'N/A'}
                  </p>
                  {productionLot?.productionOrderId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Order: {productionLot.productionOrderId}
                    </p>
                  )}
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 p-3 sm:p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <h4 className="font-medium text-green-800 dark:text-green-400 text-sm sm:text-base mb-1 sm:mb-2">Assignment Details</h4>
                  <div className="space-y-2">
                    {assignmentData.userIds && assignmentData.userIds.length > 0 && (
                      <div>
                        <span className="text-xs sm:text-sm text-green-700 dark:text-green-400 block mb-1">Assigned to Users:</span>
                        <div className="flex flex-wrap gap-1">
                          {getSelectedUserNames().map((name, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-md text-xs">
                              <User className="w-3 h-3 mr-1" />
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {assignmentData.teamIds && assignmentData.teamIds.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs sm:text-sm text-green-700 dark:text-green-400 block mb-1">Assigned to Teams:</span>
                        <div className="flex flex-wrap gap-1">
                          {getSelectedTeamNames().map((name, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-md text-xs">
                              <Users className="w-3 h-3 mr-1" />
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {assignmentResult?.taskId && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-400 text-center">
                      Task ID: <span className="font-mono font-bold">{assignmentResult.taskId}</span>
                    </p>
                    {assignmentResult?.data?.id && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Database ID: {assignmentResult.data.id}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-600">
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center">
                  This modal will close automatically in a few seconds...
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* ============ PRODUCTION LOT BASIC INFORMATION ============ */}
              {productionLot && (
                <div className="bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-800 rounded-xl overflow-hidden">
                  {/* Header with Status */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 px-4 py-3 border-b border-blue-200 dark:border-blue-800 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-gray-900 dark:text-white">Production Lot Details</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColorClass(productionLot.lifeCycleStatusCode)}`}>
                        {getStatusIcon(productionLot.lifeCycleStatusCode)}
                        {productionLot.lifeCycleStatusDescription || `Status ${productionLot.lifeCycleStatusCode || 'Unknown'}`}
                      </span>
                    </div>
                  </div>

                  {/* Basic Information Grid */}
                  <div className="p-4">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3">
                      <Hash className="w-4 h-4" />
                      Basic Information
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Production Lot ID */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Clipboard className="w-3 h-3" /> Production Lot ID
                        </span>
                        <p className="font-mono font-semibold text-blue-600 text-sm sm:text-base mt-1">
                          {productionLot.productionLotId || '-'}
                        </p>
                      </div>

                      {/* Production Lot UUID */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Database className="w-3 h-3" /> Lot UUID
                        </span>
                        <p className="font-mono text-xs text-gray-600 dark:text-gray-400 mt-1 truncate" title={productionLot.productionLotUUID}>
                          {productionLot.productionLotUUID || '-'}
                        </p>
                      </div>

                      {/* Production Order ID */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> Order ID
                        </span>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mt-1">
                          {productionLot.productionOrderId || '-'}
                        </p>
                      </div>

                      {/* Production Order UUID */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Database className="w-3 h-3" /> Order UUID
                        </span>
                        <p className="font-mono text-xs text-gray-600 dark:text-gray-400 mt-1 truncate" title={productionLot.productionOrderUUID}>
                          {productionLot.productionOrderUUID || '-'}
                        </p>
                      </div>

                      {/* Product ID */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Package className="w-3 h-3" /> Product ID
                        </span>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mt-1">
                          {productionLot.mainOutputProductId || '-'}
                        </p>
                      </div>

                      {/* Product Description */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Tag className="w-3 h-3" /> Product Description
                        </span>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 truncate" title={productionLot.mainOutputProductDescription}>
                          {productionLot.mainOutputProductDescription || 'No description'}
                        </p>
                      </div>

                      {/* Site ID */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Factory className="w-3 h-3" /> Site ID
                        </span>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mt-1">
                          {productionLot.siteId || '-'}
                        </p>
                      </div>

                      {/* Site Description */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Building className="w-3 h-3" /> Site Description
                        </span>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                          {productionLot.siteDescription || 'No description'}
                        </p>
                      </div>

                      {/* Total Quantity */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" /> Total Quantity
                        </span>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mt-1">
                          {productionLot.totalQuantity ?? '-'} {productionLot.totalQuantityUnit || ''}
                        </p>
                      </div>

                      {/* Completed Quantity */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Completed
                        </span>
                        <p className="font-semibold text-green-600 text-sm sm:text-base mt-1">
                          {productionLot.totalCompletedQuantity ?? '-'} {productionLot.totalCompletedQuantityUnit || ''}
                        </p>
                      </div>

                      {/* Status Codes */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Layers className="w-3 h-3" /> Status Codes
                        </span>
                        <div className="mt-1 space-y-1">
                          {productionLot.lifeCycleStatusCode && (
                            <span className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full inline-block mr-1">
                              LC: {productionLot.lifeCycleStatusCode}
                            </span>
                          )}
                          {productionLot.activityProcessStatusCode && (
                            <span className="text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded-full inline-block mr-1">
                              AP: {productionLot.activityProcessStatusCode}
                            </span>
                          )}
                          {productionLot.reportingPointProcessingStatusCode && (
                            <span className="text-xs bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full inline-block">
                              RP: {productionLot.reportingPointProcessingStatusCode}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Order Status */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Settings className="w-3 h-3" /> Order Status
                        </span>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                          {productionLot.productionOrderLifeCycleStatusCode || '-'}
                          {productionLot.productionOrderReleaseStatusCode && ` / Release: ${productionLot.productionOrderReleaseStatusCode}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Production Schedule */}
                  {(productionLot.productionStartDate || productionLot.productionEndDate) && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSection('schedule')}
                      >
                        <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          <CalendarClock className="w-4 h-4" />
                          Production Schedule
                        </h4>
                        <button type="button" className="text-xs text-blue-600 flex items-center gap-1">
                          {expandedSections.schedule ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {expandedSections.schedule ? 'Hide' : 'Show'}
                        </button>
                      </div>

                      {expandedSections.schedule && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                              <Clock className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <span className="text-xs text-green-700 dark:text-green-400 uppercase font-semibold">Start Date</span>
                              <p className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(productionLot.productionStartDate)}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                            <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                              <Clock className="w-4 h-4 text-red-600" />
                            </div>
                            <div>
                              <span className="text-xs text-red-700 dark:text-red-400 uppercase font-semibold">End Date</span>
                              <p className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(productionLot.productionEndDate)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Barcode Section */}
                  {productionLot.barcodeContentValue && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSection('barcode')}
                      >
                        <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          <QrCode className="w-4 h-4" />
                          Barcode Information
                        </h4>
                        <button type="button" className="text-xs text-blue-600 flex items-center gap-1">
                          {expandedSections.barcode ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {expandedSections.barcode ? 'Hide' : 'Show'}
                        </button>
                      </div>

                      {expandedSections.barcode && (
                        <div className="mt-3 flex flex-col sm:flex-row items-start gap-6">
                          <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
                            <img
                              src={`data:image/png;base64,${productionLot.barcodeContentValue}`}
                              alt={`Barcode for Production Lot ${productionLot.productionLotId}`}
                              className="max-h-20 w-auto"
                              onError={(e) => {
                                console.error('Failed to load barcode image');
                                e.target.style.display = 'none';
                                // Show fallback text
                                const fallback = document.createElement('div');
                                fallback.className = 'text-center p-3';
                                fallback.innerHTML = `
                <div class="text-sm text-gray-500 dark:text-gray-400 mb-2">Unable to display barcode</div>
                <div class="font-mono text-sm bg-gray-100 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                  ${productionLot.productionLotId}
                </div>
              `;
                                e.target.parentElement.appendChild(fallback);
                              }}
                            />
                          </div>
                          <div className="space-y-2">
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Production Lot ID</p>
                              <p className="font-mono text-lg font-bold text-gray-900 dark:text-white">
                                {productionLot.productionLotId}
                              </p>
                            </div>
                            {productionLot.barcodeBase64 && productionLot.barcodeBase64 !== productionLot.barcodeContentValue && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-2">Additional Barcode Data</p>
                                <p className="font-mono text-xs text-gray-600 dark:text-gray-400 break-all max-w-md">
                                  {productionLot.barcodeBase64.substring(0, 50)}...
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Confirmation Groups Section */}
                  {productionLot.confirmationGroups && productionLot.confirmationGroups.length > 0 && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSection('confirmationGroups')}
                      >
                        <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          <Layers className="w-4 h-4" />
                          Confirmation Groups ({productionLot.confirmationGroups.length})
                        </h4>
                        <button type="button" className="text-xs text-blue-600 flex items-center gap-1">
                          {expandedSections.confirmationGroups ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {expandedSections.confirmationGroups ? 'Hide' : 'Show'}
                        </button>
                      </div>

                      {expandedSections.confirmationGroups && productionLot.confirmationGroups.map((group, groupIndex) => (
                        <div key={group.confirmationGroupUUID || groupIndex} className="mt-3 space-y-4">
                          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <div>
                                <span className="text-xs text-indigo-600 dark:text-indigo-400">Group UUID</span>
                                <p className="font-mono text-xs truncate">{group.confirmationGroupUUID || '-'}</p>
                              </div>
                              <div>
                                <span className="text-xs text-indigo-600 dark:text-indigo-400">Processor Type</span>
                                <p className="text-sm">{group.processorObjectTypeCode || '-'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Activities */}
                          {group.activities && group.activities.length > 0 && (
                            <div className="ml-4">
                              <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Activities</h5>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs dark:text-gray-300">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="text-left px-2 py-1.5">Activity ID</th>
                                      <th className="text-left px-2 py-1.5">Description</th>
                                      <th className="text-left px-2 py-1.5">Operation</th>
                                      <th className="text-left px-2 py-1.5">Duration</th>
                                      <th className="text-left px-2 py-1.5">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.activities.map((activity, idx) => (
                                      <tr key={idx} className="hover:bg-blue-50 dark:hover:bg-blue-900/20">
                                        <td className="px-2 py-1.5 font-mono text-blue-600">{activity.activityId || '-'}</td>
                                        <td className="px-2 py-1.5">{activity.activityDescription || '-'}</td>
                                        <td className="px-2 py-1.5">{activity.operationId || '-'}</td>
                                        <td className="px-2 py-1.5">{formatDuration(activity.plannedDuration)}</td>
                                        <td className="px-2 py-1.5">
                                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${activity.confirmationCompletedIndicator
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                                            }`}>
                                            {activity.confirmationCompletedIndicator ? 'Completed' : 'Pending'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Multi-User Selection */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Assign to Users (Optional)
                      {fetchingUsers && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Loading users...
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Quick actions */}
                  {users.length > 0 && assignmentData.userIds?.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setAssignmentData(prev => ({ ...prev, userIds: [] }))}
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 px-2 py-1 rounded"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Selected Users Tags */}
                {assignmentData.userIds && assignmentData.userIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {assignmentData.userIds.map(userId => {
                      const user = users.find(u => u.id === userId);
                      return user ? (
                        <span
                          key={userId}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-800 shadow-sm"
                        >
                          <User className="w-3 h-3" />
                          <span className="max-w-[150px] truncate">{user.name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setAssignmentData(prev => ({
                                ...prev,
                                userIds: prev.userIds.filter(id => id !== userId)
                              }));
                            }}
                            className="ml-1 p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full transition-colors"
                            title="Remove user"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* User Multi-Select Dropdown */}
                <select
                  value=""
                  onChange={(e) => {
                    const selectedUserId = e.target.value;
                    if (selectedUserId && !assignmentData.userIds?.includes(selectedUserId)) {
                      setAssignmentData(prev => ({
                        ...prev,
                        userIds: [...(prev.userIds || []), selectedUserId]
                      }));
                    }
                    // Reset the select to show placeholder again
                    e.target.value = '';
                  }}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm dark:bg-gray-700 dark:text-white"
                  disabled={loading || fetchingUsers}
                >
                  <option value="">-- Add a user --</option>
                  {!fetchingUsers && users.length === 0 ? (
                    <option value="" disabled>No users available</option>
                  ) : (
                    users
                      .filter(user => !assignmentData.userIds?.includes(user.id))
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))
                  )}
                </select>

                {assignmentData.userIds?.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {assignmentData.userIds.length} user{assignmentData.userIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {/* Multi-Team Selection */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Assign to Teams (Optional)
                      {fetchingTeams && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Loading teams...
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Quick actions */}
                  {teams.length > 0 && assignmentData.teamIds?.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setAssignmentData(prev => ({ ...prev, teamIds: [] }))}
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 px-2 py-1 rounded"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Selected Teams Tags */}
                {assignmentData.teamIds && assignmentData.teamIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {assignmentData.teamIds.map(teamId => {
                      const team = teams.find(t => t.id === teamId);
                      return team ? (
                        <span
                          key={teamId}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium border border-purple-200 dark:border-purple-800 shadow-sm"
                        >
                          <Users className="w-3 h-3" />
                          <span className="max-w-[150px] truncate">{team.name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setAssignmentData(prev => ({
                                ...prev,
                                teamIds: prev.teamIds.filter(id => id !== teamId)
                              }));
                            }}
                            className="ml-1 p-0.5 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full transition-colors"
                            title="Remove team"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Team Multi-Select Dropdown */}
                <select
                  value=""
                  onChange={(e) => {
                    const selectedTeamId = e.target.value;
                    if (selectedTeamId && !assignmentData.teamIds?.includes(selectedTeamId)) {
                      setAssignmentData(prev => ({
                        ...prev,
                        teamIds: [...(prev.teamIds || []), selectedTeamId]
                      }));
                    }
                    // Reset the select to show placeholder again
                    e.target.value = '';
                  }}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm dark:bg-gray-700 dark:text-white"
                  disabled={loading || fetchingTeams}
                >
                  <option value="">-- Add a team --</option>
                  {!fetchingTeams && teams.length === 0 ? (
                    <option value="" disabled>No teams available</option>
                  ) : (
                    teams
                      .filter(team => !assignmentData.teamIds?.includes(team.id))
                      .map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))
                  )}
                </select>

                {assignmentData.teamIds?.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {assignmentData.teamIds.length} team{assignmentData.teamIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {/* Assignment Summary */}
              {(assignmentData.userIds?.length > 0 || assignmentData.teamIds?.length > 0) && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Assignment Summary
                  </h4>
                  <div className="space-y-3">
                    {assignmentData.userIds?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-blue-700 dark:text-blue-400 uppercase tracking-wider">Users ({assignmentData.userIds.length})</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {assignmentData.userIds.map(userId => {
                            const user = users.find(u => u.id === userId);
                            return user ? (
                              <span key={userId} className="text-xs bg-white dark:bg-gray-700 px-2 py-1 rounded border border-blue-200 dark:border-blue-800 text-gray-700 dark:text-gray-300">
                                {user.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                    {assignmentData.teamIds?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-purple-700 dark:text-purple-400 uppercase tracking-wider">Teams ({assignmentData.teamIds.length})</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {assignmentData.teamIds.map(teamId => {
                            const team = teams.find(t => t.id === teamId);
                            return team ? (
                              <span key={teamId} className="text-xs bg-white dark:bg-gray-700 px-2 py-1 rounded border border-purple-200 dark:border-purple-800 text-gray-700 dark:text-gray-300">
                                {team.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assignment Info */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-400 font-semibold mb-2">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  This action will:
                </p>
                <ul className="space-y-1 text-xs sm:text-sm text-yellow-700 dark:text-yellow-400">
                  <li>• Import the production task from SAP into the database</li>
                  <li>• Assign the task to the selected users and/or teams</li>
                  <li>• Make the task visible in the task management system</li>
                </ul>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-400 text-sm">Assignment Failed</p>
                      <p className="text-xs sm:text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 sm:px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || (assignmentData.userIds?.length === 0 && assignmentData.teamIds?.length === 0) || fetchingUsers || fetchingTeams}
                  className="px-4 sm:px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Users className="w-4 h-4" />
                      Import & Assign Task
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignTaskModal;